// src/AuthContext.tsx
import {
  createContext, useContext, useState, useEffect, useCallback,
  type ReactNode,
} from 'react';
import {
  getSession, sendCode as apiSendCode, verifyCode as apiVerifyCode,
  registerAuthor as apiRegister, signOutAuthor as apiSignOut,
  oauthSignIn as apiOAuthSignIn, oauthRegister as apiOAuthRegister,
  type AuthorSession,
} from './auth';

interface AuthContextValue {
  author:         AuthorSession | null;
  loading:        boolean;
  authLoading:    boolean;
  sendCode:       (email: string, mode: 'signin' | 'register') => Promise<void>;
  signIn:         (email: string, code: string) => Promise<void>;
  register:       (email: string, code: string) => Promise<void>;
  /** Signs in with a Firebase ID token; resolves to the verified email when there's no account yet (then call oauthRegister). */
  oauthSignIn:    (idToken: string) => Promise<{ needsRegistration: true; email: string } | null>;
  oauthRegister:  (idToken: string) => Promise<void>;
  signOut:        () => Promise<void>;
  /** Re-fetches /api/auth/me and updates the cached session — call after an admin-set field (e.g. aiEnabled) may have changed. */
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// Caches only the non-sensitive display fields (email/isAdmin) so the
// UI can render optimistically without waiting on a round-trip. The actual
// credential lives in the HttpOnly __ring_auth cookie, which JS can never read
// (and never needs to) — the server is still the source of truth on every
// real request; this cache just avoids a blank/loading flash on next visit.
const SESSION_KEY = 'ring_auth_session';

function readCachedSession(): AuthorSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthorSession) : null;
  } catch {
    return null;
  }
}

function writeCachedSession(session: AuthorSession | null) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [author, setAuthor]           = useState<AuthorSession | null>(readCachedSession);
  const [loading, setLoading]         = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    getSession()
      .then(session => { setAuthor(session); writeCachedSession(session); })
      .finally(() => setLoading(false));
  }, []);

  const sendCode = useCallback(async (email: string, mode: 'signin' | 'register') => {
    setAuthLoading(true);
    try { await apiSendCode(email, mode); }
    finally { setAuthLoading(false); }
  }, []);

  const signIn = useCallback(async (email: string, code: string) => {
    setAuthLoading(true);
    try {
      const session = await apiVerifyCode(email, code);
      setAuthor(session);
      writeCachedSession(session);
    } finally { setAuthLoading(false); }
  }, []);

  const register = useCallback(async (email: string, code: string) => {
    setAuthLoading(true);
    try {
      const session = await apiRegister(email, code);
      setAuthor(session);
      writeCachedSession(session);
    } finally { setAuthLoading(false); }
  }, []);

  const oauthSignIn = useCallback(async (idToken: string) => {
    setAuthLoading(true);
    try {
      const result = await apiOAuthSignIn(idToken);
      if ('needsRegistration' in result) return result;
      setAuthor(result);
      writeCachedSession(result);
      return null;
    } finally { setAuthLoading(false); }
  }, []);

  const oauthRegister = useCallback(async (idToken: string) => {
    setAuthLoading(true);
    try {
      const session = await apiOAuthRegister(idToken);
      setAuthor(session);
      writeCachedSession(session);
    } finally { setAuthLoading(false); }
  }, []);

  const signOut = useCallback(async () => {
    setAuthLoading(true);
    try {
      await apiSignOut();
      setAuthor(null);
      writeCachedSession(null);
    } finally { setAuthLoading(false); }
  }, []);

  const refreshSession = useCallback(async () => {
    const session = await getSession();
    setAuthor(session);
    writeCachedSession(session);
  }, []);

  return (
    <AuthContext.Provider value={{ author, loading, authLoading, sendCode, signIn, register, oauthSignIn, oauthRegister, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}
